/* eslint-disable string-to-lingui/missing-lingui-transformation */

import {t} from '@lingui/macro'
import type React from 'react'
import Helmet from 'react-helmet'
import styled from 'styled-components'

import {Accordion, LoadingPage, Stack, Text, tokens} from '@pleo-io/telescope'

import {ErrorPage} from '@product-web/error/error-boundary'
import NotFoundError from '@product-web/error/error-not-found'
import {useFlags, useFlagsLoaded} from '@product-web/flags'
import {getHelpCentreArticleLink} from '@product-web/locale/helpers'
import {getIsFdd, useCompanyUser} from '@product-web/shell/user'
import {exhaustiveCheck} from '@product-web/utils'
import {useMediaQuery} from '@product-web/web-platform/use-media-query'

import {Container, MainSectionWrapper, Page} from '~/generic-ui/page'
import config from 'config'
import {breakpoints} from 'styles/theme'

import {ProgressIndicator} from './progress-indicator'

import {bff} from '../../bff-hooks'
import {FooterCard} from '../../components/footer-card'
import {MilestoneCard} from '../../components/milestone-card'
import illustration from '../../images/person-with-book.svg'

//#region Components

export const GetStartedScreen = () => {
    const {isPenguinsMilestone1Live} = useFlags()
    const flagsLoaded = useFlagsLoaded()
    const {data: footerData, isLoading: isFooterDataLoading} =
        bff.onboarding.getStarted.getFooterData.useQuery()

    if (!flagsLoaded || isFooterDataLoading) {
        return <LoadingPage />
    }

    if (!isPenguinsMilestone1Live) {
        return <NotFoundError />
    }

    const openLinkInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener, noreferrer')
    }

    // Fall back to the DEMO_PLAYGROUND variant when there is no data available so the user sees a demo card
    const demoCardVariant = footerData?.demoVariant ?? 'DEMO_PLAYGROUND'

    const demoCardDestinationUrl = getFooterCardDestinationUrl(demoCardVariant)
    const helpCentreCardDestinationUrl = getFooterCardDestinationUrl('HELP_CENTRE')

    return (
        <GridContainer>
            <Helmet>
                <title>{t`Get Started`}</title>
            </Helmet>

            <Page layout="wide" showNav>
                <GetStartedScreenPageContent />
            </Page>

            <FooterSection>
                <InnerFooter>
                    <FooterCard
                        variant="HELP_CENTRE"
                        onClick={() => openLinkInNewTab(helpCentreCardDestinationUrl)}
                        headingLevel="h3"
                    />

                    <FooterCard
                        variant={demoCardVariant}
                        onClick={() => openLinkInNewTab(demoCardDestinationUrl)}
                        headingLevel="h3"
                    />
                </InnerFooter>
            </FooterSection>
        </GridContainer>
    )
}

const GetStartedScreenPageContent = () => {
    const user = useCompanyUser()
    const {data, error: dataError} = bff.onboarding.getStarted.getData.useQuery({
        isFdd: getIsFdd(user),
    })
    const isSmallScreen = useMediaQuery(`(width < ${breakpoints.mediumTabletUp})`)

    if (dataError) {
        return <ErrorPage />
    }

    if (!data) {
        return <LoadingPage />
    }

    const firstIncompleteGroup = data.milestoneGroups.find((group) => group.status !== 'COMPLETE')
    const lastGroup = data.milestoneGroups.slice(-1)[0]
    const activeGroup = firstIncompleteGroup ?? lastGroup

    return (
        <div>
            <PageHeader>
                <Page.Title>Get Started</Page.Title>
            </PageHeader>

            <PageContent>
                <IllustrationSection>
                    {!isSmallScreen && <Illustration src={illustration} alt="" />}

                    <Text as="h2" variant="2xlarge-accent" weight="medium">
                        Discover a new way to manage expenses
                    </Text>
                </IllustrationSection>

                <Stack space={24} as="section" stretch>
                    <ProgressIndicator
                        headingLevel="h3"
                        steps={data.milestoneGroups.map((group) => ({
                            label: getProgressIndicatorLabel(group.name),
                            isCompleted: group.status === 'COMPLETE',
                            isCurrentStep: group === activeGroup,
                        }))}
                    />

                    {activeGroup.name === 'TRIAL' && (
                        <Stack space={16} as="section" stretch>
                            {activeGroup.milestones.map((milestone) => {
                                const firstIncompleteMilestone = activeGroup.milestones.find(
                                    (m) => m.status !== 'COMPLETE',
                                )

                                return (
                                    <MilestoneCard
                                        key={milestone.name}
                                        headingLevel="h4"
                                        milestone={milestone.name}
                                        status={milestone.status}
                                        disabled={milestone.disabled}
                                        active={milestone === firstIncompleteMilestone}
                                    />
                                )
                            })}
                        </Stack>
                    )}

                    {activeGroup.name === 'EXPLORE' && (
                        <Accordion>
                            {activeGroup.milestones.map((milestone) => (
                                <Accordion.Item key={milestone.name} value={milestone.name}>
                                    <Accordion.Header headingLevel="h2" heading={milestone.name} />

                                    <Accordion.Content>
                                        {milestone.tasks.map((task) => (
                                            <Text key={task.name}>
                                                {task.name} ({task.status})
                                            </Text>
                                        ))}
                                    </Accordion.Content>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </Stack>
            </PageContent>
        </div>
    )
}

//#endregion Components

//#region Helpers

type MilestoneGroupName = 'TRIAL' | 'EXPLORE'

const getProgressIndicatorLabel = (value: MilestoneGroupName) => {
    switch (value) {
        case 'TRIAL':
            return 'Start trial'
        case 'EXPLORE':
            return 'Explore Pleo'

        default:
            return exhaustiveCheck(value)
    }
}

type CardVariant = React.ComponentProps<typeof FooterCard>['variant']

export const getFooterCardDestinationUrl = (cardVariant: CardVariant) => {
    // TODO: Tom to confirm destination URLs
    switch (cardVariant) {
        case 'HELP_CENTRE':
            return getHelpCentreArticleLink()
        case 'DEMO_PLAYGROUND':
            return `${config.endpoints.commercial}/explore-product`
        case 'DEMO_SALES':
            return `${config.endpoints.commercial}/demo`
        default:
            return exhaustiveCheck(cardVariant)
    }
}

//#endregion Helpers

//#region Styles

const GridContainer = styled.div`
    box-sizing: border-box;
    display: grid;
    grid-template-rows: 1fr auto;
    width: 100%;
    height: 100vh;
    overflow: auto;

    * {
        box-sizing: border-box;
    }

    ${Container}, ${MainSectionWrapper} {
        height: auto;
    }
`

const PageHeader = styled(Page.Header)`
    margin: 0 auto;
    max-width: 1200px;
`

// Using media queries instead of container queries to match padding and
// behavior of the generic-ui page
const PageContent = styled.div`
    display: grid;
    row-gap: ${tokens.spacing24};
    column-gap: ${tokens.spacing56};
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    margin: 0 auto;
    max-width: 1200px;

    @media (width >= ${breakpoints.mediumTabletUp}) {
        grid-template-columns: minmax(10px, 1fr) 2fr;
        grid-template-rows: 1fr;
        gap: ${tokens.spacing56};
    }

    @media (width >= ${breakpoints.desktopUp}) {
        /* Not sure why, but on bigger sizes, the generic-ui page doesn't have a bottom padding */
        padding-bottom: ${tokens.spacing56};
    }
`

const IllustrationSection = styled.section`
    max-width: 360px;
`

const Illustration = styled.img`
    display: block;
    width: 100%;
    max-width: 320px;
`

const FooterSection = styled.section`
    display: flex;
    justify-content: center;
    padding: ${tokens.spacing24};
    background-color: ${tokens.colorBackgroundStaticLouder};

    @media (width >= ${breakpoints.smallTabletUp}) {
        padding: ${tokens.spacing40};
    }

    @media (width >= ${breakpoints.desktopUp}) {
        padding: ${tokens.spacing56};
    }
`

const InnerFooter = styled.div`
    display: flex;
    flex-direction: column;
    column-gap: ${tokens.spacing24};
    row-gap: ${tokens.spacing24};
    width: 100%;
    max-width: 1200px;

    @media (width >= ${breakpoints.mediumTabletUp}) {
        flex-direction: row;
        column-gap: ${tokens.spacing56};
    }

    > * {
        flex: 1;
    }
`

//#endregion Styles
